import { TabSkeleton } from "../BotManagement/BotManagementSkeleton";
import ConversionCell from "../../../components/UI/ConversionCell";
import {
    Plus, ArrowDown, Pencil, Copy, Trash2, DollarSign, Zap,
} from 'lucide-react';
import { useNotification } from "../../../components/Notifications/NotificationContext";
import { authService } from "../../../services/api";
import DownsellDefaultDeliveryCard from "./DownsellDefaultDeliveryCard";

export default function DownsellList({ downsells, loading, onEdit, onDelete, onDuplicate, onNew, botId, revenueSymbol = 'R$', defaultDelivery, onDefaultDeliveryChange }) {
    const { showError, showSuccess } = useNotification();

    // Faturamento é SEMPRE em BRL (mesmo padrão de dashboard/sales) → revenueSymbol ('R$').
    const fmtMoney = (v) => {
        const n = Number(v || 0);
        const sep = revenueSymbol.length > 1 ? ' ' : '';
        return `${revenueSymbol}${sep}${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Deseja realmente deletar este downsell?")) return;

        try {
            const { data } = await authService.downsell.delete_downsell(botId, id);

            if (!data.success) throw new Error(data.error);

            showSuccess("Downsell deletado!");
            onDelete();
        } catch (err) {
            showError(err?.response?.data?.error || err.message);
        }
    };

    const handleDuplicate = async (id) => {
        try {
            const { data } = await authService.downsell.duplicate_downsell(botId, id);

            if (!data.success) throw new Error(data.error);

            showSuccess("Downsell duplicado!");
            onDuplicate?.();
        } catch (err) {
            showError(err?.response?.data?.error || err.message);
        }
    };

    return (
        <>
            {loading ? (
                <TabSkeleton tab="downsell" />
            ) : (
                <div className="mt-6 flex flex-col lg:flex-row gap-6 items-start spt-reveal">
                  <div className="flex-1 min-w-0 w-full">

                    {downsells.length === 0 && (
                        <div className="empty-state">
                            <ArrowDown size={28} className="inline" />
                            <h4>Nenhum downsell criado</h4>
                            <p>Crie um novo downsell para aumentar sua conversão.</p>
                        </div>
                    )}

                    {/* DESKTOP */}
                    {downsells.length > 0 && (
                        <div className="sx-desktop">
                            <table className="sx-table">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Desconto</th>
                                        <th>Delay</th>
                                        <th>Faturamento</th>
                                        <th>Conversão</th>
                                        {/* <th>Status</th> */}
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {downsells.map(ds => (
                                        <tr key={ds.id}>
                                            <td className="max-w-[180px] truncate" title={ds.name}>{ds.name}</td>
                                            <td>{ds.discount_percentage}%</td>
                                            <td>{ds.delay_minutes} min</td>
                                            <td className="font-semibold text-green-600 whitespace-nowrap">{fmtMoney(ds.revenue)}</td>
                                            <td className="whitespace-nowrap"><ConversionCell paid={ds.paid_count ?? 0} generated={ds.generated_count ?? 0} /></td>
                                            {/* <td>{ds.is_active ? <span class="sx-pill sx-pill--green"><span class="sx-pill-dot"></span>Ativo</span> : <span class="sx-pill sx-pill--red"><span class="sx-pill-dot"></span>Inativo</span>}</td> */}
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDuplicate(ds.id)}
                                                        className="btn-secondary btn-action"
                                                        title="Duplicar downsell"
                                                    >
                                                        <Copy size={16} />
                                                    </button>
                                                    <button type="button" onClick={() => onEdit(ds)} className="btn-primary btn-action" title="Editar downsell">
                                                        <Pencil size={16} />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="btn-delete-2 btn-action"
                                                        onClick={() => handleDelete(ds.id)}
                                                        title="Excluir downsell"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>)}

                    {/* MOBILE — inspirado nos Redirecionadores: lista contínua (sem card avulso),
                        chip roxo, pills e métricas em grid, no padrão de cores da Spectrum. */}
                    <div className="sx-mobile">
                        <div className="rounded-2xl border border-white/[0.08] overflow-hidden">
                            {downsells.map((ds, index) => (
                                <div
                                    key={ds.id}
                                    className={`p-4 ${index > 0 ? 'border-t border-white/[0.06]' : ''} ${ds.is_active ? '' : 'opacity-60'}`}
                                >
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-xl bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.18)] flex items-center justify-center text-[#a78bfa] font-bold text-sm flex-shrink-0" title="Desconto">
                                                {ds.discount_percentage}%
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-semibold text-white truncate" title={ds.name}>{ds.name}</div>
                                                <div className="flex items-center gap-2 flex-wrap mt-1">
                                                    <span className="sx-pill sx-pill--purple">{ds.delay_minutes === 0 ? 'Imediato' : `${ds.delay_minutes} min`}</span>
                                                    {ds.is_active
                                                        ? <span className="sx-pill sx-pill--green"><span className="sx-pill-dot"></span>Ativo</span>
                                                        : <span className="sx-pill sx-pill--red">Inativo</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 mb-3">
                                        <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] px-3 py-2 min-w-0">
                                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-white/35">
                                                <DollarSign size={11} className="text-[#4ade80]" /> Faturamento
                                            </div>
                                            <div className="text-sm font-bold text-[#4ade80] truncate">{fmtMoney(ds.revenue)}</div>
                                        </div>
                                        <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] px-3 py-2 min-w-0">
                                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-white/35">
                                                <Zap size={11} className="text-[#60a5fa]" /> Conversão
                                            </div>
                                            <div className="text-sm font-bold text-white truncate"><ConversionCell paid={ds.paid_count ?? 0} generated={ds.generated_count ?? 0} /></div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button type="button" onClick={() => onEdit(ds)} className="btn-primary btn-action flex-1 justify-center">
                                            <Pencil size={14} /> Editar
                                        </button>
                                        <button type="button" onClick={() => handleDuplicate(ds.id)} className="btn-secondary btn-action flex-1 justify-center">
                                            <Copy size={14} /> Duplicar
                                        </button>
                                        <button type="button" onClick={() => handleDelete(ds.id)} className="btn-delete-2 btn-action" title="Excluir downsell">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onNew}
                        className="w-full flex items-center justify-center gap-2 py-2.5 mt-4 rounded-xl border border-dashed border-white/[0.08] text-white/40 hover:text-[var(--purple)] hover:border-[var(--purple-dark)] hover:bg-[var(--bg-primary)] transition-colors text-sm"
                    >
                        <Plus size={16} /> Novo Downsell
                    </button>

                  </div>

                  <aside className="w-full lg:w-[340px] shrink-0">
                    <DownsellDefaultDeliveryCard
                        botId={botId}
                        value={defaultDelivery}
                        onChange={onDefaultDeliveryChange}
                    />
                  </aside>

                </div>
            )
            }
        </>
    );
}
